/// <amd-module name="Dashboards/ConfigHost/ConfigPanel" />
import * as React from "react";
import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { Dialog } from "VSSUI/Dialog";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";

import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { InlineError } from "Dashboards/CommonConfig/InlineError";
import * as Resources from "Dashboards/ConfigHost/Resources";
import { WidgetCatalogQuery } from "Dashboards/WidgetCatalog/WidgetCatalogQuery";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { ConfigPanelProps } from "Dashboards/WidgetContracts/Internal/ConfigPanelProps";
import { ContributionConfigConstraints, LoadedEventInfo, LoadingStatus } from "Dashboards/WidgetContracts/Internal/ConfigProps";
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WidgetCatalogEntry } from "Dashboards/WidgetContracts/Internal/WidgetCatalogEntry";
import { ConfigHost } from "./ConfigHost";
import { publishConfigHostLoaded, publishConfigHostLoadFailed } from "./ConfigHostTelemetry";
import { getPanelWidth } from "./WideConfigPanelPolicy";

export interface ConfigPanelState {
    widgetCatalogEntries: WidgetCatalogEntry[] | undefined;
    currentConfig: DashboardWidgetProps | undefined;
    savedConfig: DashboardWidgetProps | undefined;
    configUiLoadingOutcome: LoadedEventInfo;
    isPendingChangesDialogOpen: boolean;
    hasChangeOccurred: boolean;
    saveValidationTimeStamp?: Date;

    showHasErrorsMessage: boolean;
}

/** This panel is used to allow a user to (re)configure a widget. */
export class ConfigPanel extends VssComponent<ConfigPanelProps, ConfigPanelState> {
    constructor(props: ConfigPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            widgetCatalogEntries: undefined,
            currentConfig: props.config,
            savedConfig: props.config,
            isPendingChangesDialogOpen: false,
            hasChangeOccurred: false,
            configUiLoadingOutcome: { outcome: LoadingStatus.loading, outcomeDescription: undefined },
            saveValidationTimeStamp: undefined,
            showHasErrorsMessage: false
        };
    }

    public async componentDidMount(): Promise<void> {
        this.onOpenConfig();
        const queryCache = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        const query = new WidgetCatalogQuery(this.props.projectId);
        const queryResult = await queryCache.getCacheableQueryResult(query);
        this.setState({ widgetCatalogEntries: queryResult.catalogEntries });
    }

    public render(): JSX.Element | null {
        let saveBtnDisableStatus = (!this.state.hasChangeOccurred);
        if(this.state.showHasErrorsMessage && this.state.hasChangeOccurred){
            saveBtnDisableStatus = true;
        }
        const footerButtons: IButtonProps[] = [
            {
                className: "widget-config-save",
                onClick: this.onSave,
                text: Resources.WidgetConfig_SaveText,
                // Save button will appear as long as one change has been made, regardless of if it is consistent with baseline.
                // The save button is permitted in general, to allow user the bowtie legacy validation highlights pattern.
                disabled: saveBtnDisableStatus,
                primary: true
            },
            {
                className: "widget-config-close",
                onClick: this.onDismiss,
                text: Resources.WidgetConfig_CloseText
            }
        ];
        return (
            <Panel
                className="dashboard-widget-config-panel"
                contentClassName={getPanelWidth(this.getCurrentCatalogEntry()!)}
                size={ContentSize.Auto}
                titleProps={{ text: Resources.WidgetConfig_HeaderText }}
                onDismiss={this.onDismiss}
                footerButtonProps={footerButtons}
            >
                {this.isLoading() && <Spinner className="absolute-fill" />}
                <div className="flex-column flex-grow flex-noshrink">
                    {this.state.showHasErrorsMessage && <InlineError message={Resources.OnSaveError} />}
                    {this.state.configUiLoadingOutcome.outcome === LoadingStatus.failure && (
                        <InlineError
                            message={
                                this.state.configUiLoadingOutcome.outcomeDescription &&
                                this.state.configUiLoadingOutcome.outcomeDescription.toString()
                            }
                        />
                    )}
                    {this.isReadyToRenderHost() && (
                        <div className={css("host-area", this.state.configUiLoadingOutcome.outcome === LoadingStatus.loading && "invisible")}>
                            <ConfigHost
                                key={this.state.currentConfig!.contributionId}
                                contributionConstraints={this.assembleContributedProps()}
                                config={this.state.currentConfig!}
                                parentProps={{
                                    onUpdate: this.onUpdate,
                                    onConfigLoaded: this.onLoaded,
                                    onValidationResponse: this.onValidationResponse
                                }}
                                saveValidationTimeStamp={this.state.saveValidationTimeStamp}
                                teamContext={this.props.teamContext}
                            />
                        </div>
                    )}
                </div>

                {this.state.isPendingChangesDialogOpen && (
                    <Dialog
                        className="widget-pending-changes-dialog"
                        titleProps={{ text: Resources.PendingChanges_Title }}
                        footerButtonProps={[
                            {
                                text: Resources.PendingChanges_OK,
                                primary: true,
                                onClick: this.onDismissConfirmed
                            },
                            {
                                text: Resources.PendingChanges_Cancel,
                                onClick: this.onDismissCancelled
                            }
                        ]}
                        onDismiss={this.onDismissCancelled}
                    >
                        {Resources.PendingChanges_Message}
                    </Dialog>
                )}
            </Panel>
        );
    }

    private isLoading(): boolean {
        return (
            this.state.widgetCatalogEntries == null ||
            this.state.currentConfig == null ||
            this.state.configUiLoadingOutcome.outcome === LoadingStatus.loading
        );
    }

    private isReadyToRenderHost(): boolean {
        return this.state.widgetCatalogEntries != null && this.state.currentConfig != null;
    }

    private getCurrentCatalogEntry(): WidgetCatalogEntry | undefined {
        if (this.state.currentConfig == null || this.state.widgetCatalogEntries == null) {
            return undefined;
        }

        const contributionId = this.state.currentConfig.contributionId;
        return this.state.widgetCatalogEntries.find((item: WidgetCatalogEntry) => {
            return item.contributionId === contributionId;
        });
    }

    private assembleContributedProps(): ContributionConfigConstraints {
        const catalogEntry: WidgetCatalogEntry | undefined = this.getCurrentCatalogEntry();
        if (!catalogEntry) {
            throw Error(format(`No Catalog Entry found for Type: {0}`, this.props.config.contributionId));
        }

        const contributedProps: ContributionConfigConstraints = {
            supportedSizes: catalogEntry.supportedSizes,
            isNameConfigurable: catalogEntry.isNameConfigurable
        };
        return contributedProps;
    } 

    private onUpdate = (newWidgetConfig: DashboardWidgetProps): void => {
        const revisedConfig = this.generateRevisedConfig(newWidgetConfig);
        if (revisedConfig.hasError == undefined) {
            revisedConfig.hasError = false;
        }
        this.setState({
            currentConfig: revisedConfig,
            hasChangeOccurred: true,
            showHasErrorsMessage: revisedConfig.hasError // reset error header
        });
          this.props.parentProps.onUpdate(revisedConfig);
    };

    private onDismiss = (): void => {
        const hasPendingChanges = JSON.stringify(this.state.savedConfig) !== JSON.stringify(this.state.currentConfig);
        if (hasPendingChanges) {
            // Tell the host page to momentarily suppress widget preview focus mode, while the dismiss dialog is appearing.
            if (this.props.parentProps.onTogglePreviewHighlighting) {
                this.props.parentProps.onTogglePreviewHighlighting(false);
            }
            this.setState({ isPendingChangesDialogOpen: true });
        } else {
            this.props.parentProps.onDismiss();
        }
    };
    private onDismissCancelled = (): void => {
        this.setState({ isPendingChangesDialogOpen: false });
        if (this.props.parentProps.onTogglePreviewHighlighting) {
            this.props.parentProps.onTogglePreviewHighlighting(true);
        }
    };

    private onDismissConfirmed = (): void => {
        this.setState({ isPendingChangesDialogOpen: false });
        if (this.props.parentProps.onTogglePreviewHighlighting) {
            this.props.parentProps.onTogglePreviewHighlighting(true);
        }
        this.props.parentProps.onDismiss();
    };

    private onSave = (): void => {
        this.setState({ saveValidationTimeStamp: new Date() });
    };

    private onValidationResponse = (newWidgetConfig: DashboardWidgetProps, isValidToSave: boolean): void => {
        if (isValidToSave) {
            const newConfig = this.generateRevisedConfig(newWidgetConfig);
            this.setState({ currentConfig: newConfig }, this.onSaveApproved);
        } else {
            this.setState({ showHasErrorsMessage: true });
        }
    };

    /** Creates a new config snapshot using the user-customizable aspects of config.
     * This step protects against potential tampering of non-customizable fields such as ID, as well as ensuring settingsVersion is not discarded by configs which don't return it.*/
    private generateRevisedConfig(incomingConfig: DashboardWidgetProps): DashboardWidgetProps {
        const revisedConfig = { ...this.props.config } as DashboardWidgetProps;
        revisedConfig.name = incomingConfig.name;
        if (incomingConfig.size != null) {
            revisedConfig.size = incomingConfig.size;
        }
        revisedConfig.settings = incomingConfig.settings;
        revisedConfig.hasError = incomingConfig.hasError;
        if (incomingConfig.settingsVersion) {
            revisedConfig.settingsVersion = incomingConfig.settingsVersion;
        } else {
            revisedConfig.settingsVersion = {
                major: 1,
                minor: 0,
                patch: 0
            };
        }
        return revisedConfig;
    } 

    /** Config Host must actively ensure the custom & general config is ready to save.  */
    private onSaveApproved = (): void => {
        this.props.parentProps.onSave(this.state.currentConfig!);
        this.onSaveConfig();
    };

    private onLoaded = (loadingOutcome?: LoadedEventInfo): void => {
        if (this.state.configUiLoadingOutcome.outcome === LoadingStatus.loading) {
            const defaultOutcome: LoadedEventInfo = {
                outcome: LoadingStatus.success
            };
            this.setState({
                configUiLoadingOutcome: loadingOutcome || defaultOutcome
            });
            this.logConfigLoadOutcome(loadingOutcome || defaultOutcome);
        }
    };

    private logConfigLoadOutcome(loadingOutcome: LoadedEventInfo) {
        const eventInformation = {
            widgetId: this.props.config.id,
            contributionId: this.props.config.contributionId,
            configContributionId: this.props.config.configurationContributionId
        };
        if (loadingOutcome.outcome === LoadingStatus.success) {
            publishConfigHostLoaded(this.context.pageContext, eventInformation);
        } else {
            publishConfigHostLoadFailed(this.context.pageContext, {
                ...eventInformation,
                error: loadingOutcome.outcomeDescription
            });
        }
    }

    private onOpenConfig(): void {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("Dashboards", "Dashboards.Config.Open", {
            isNewPlatform: true
        });
    }

    private onSaveConfig(): void {
        // Note: Ths page can ignore a save request.
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("Dashboards", "Dashboards.Config.Save", {
            isNewPlatform: true,
            contributionId: this.props.config.contributionId
        });
    }
}

VssComponent.register("ConfigPanel", ConfigPanel);
